import { BasicMonitorStatus, BarInterval, RankingAlgorithm, MonitorStatus } from '@/services/enums';
import { create } from 'zustand';
import { klineMonitorService, MonitorTask } from '../services/klineMonitorService';
import { useToastStore } from './useToastStore';

interface KlineMonitorState {
  // 状态
  monitors: MonitorTask[];
  isLoading: boolean;
  error: string | null;
  stoppingMonitors: Set<string>; // 正在停止的监控任务ID
  startingMonitors: Set<string>; // 正在启动的监控任务ID

  // Actions
  refreshMonitors: (symbol?: string, status?: MonitorStatus) => Promise<void>;
  silentRefreshMonitors: (symbol?: string, status?: MonitorStatus) => Promise<void>; // 静默刷新（不显示加载状态）
  stopMonitor: (monitorId: string) => Promise<void>;
  startMonitor: (monitorId: string, symbol: string, bar: BarInterval, enableAutoTrading?: boolean) => Promise<void>;
  deleteMonitor: (monitorId: string) => Promise<void>;
  deleteStoppedMonitors: () => Promise<number>;
  updateMonitorStatus: (symbol: string, bar: BarInterval, status: BasicMonitorStatus, monitorId?: string) => void;
  clearError: () => void;

  getMonitorStatus: (monitorId: string) => Promise<MonitorTask>;

  // Auto Monitor Actions
  getAutoMonitorStatus: () => Promise<any>;
  getAutoMonitorConfig: () => Promise<any>;
  startAutoMonitor: (
    enableAutoTrading: boolean,
    monitorCount: number,
    excludedSymbols: string[],
    systemPromptId?: string,
    tradingParams?: any,
    minVolume?: number,
    rankingAlgorithm?: RankingAlgorithm,
  ) => Promise<void>;
  stopAutoMonitor: () => Promise<void>;
}

export const useKlineMonitorStore = create<KlineMonitorState>((set, get) => ({
  // 初始状态
  monitors: [],
  isLoading: false,
  error: null,
  stoppingMonitors: new Set(),
  startingMonitors: new Set(),

  // 刷新监控任务列表（添加防重复请求保护）
  refreshMonitors: async (symbol?: string, status?: MonitorStatus) => {
    const state = get();
    // 如果正在加载，跳过重复请求
    if (state.isLoading) {
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const monitors = await klineMonitorService.getAllRunningMonitors(status, symbol);
      set({ monitors, isLoading: false });
    } catch (error: any) {
      const errorMessage = error.message || '获取监控任务列表失败';
      set({ error: errorMessage, isLoading: false });
      useToastStore.getState().show(errorMessage, 'error');
    }
  },

  // 静默刷新监控任务列表（不显示加载状态，不显示错误提示）
  silentRefreshMonitors: async (symbol?: string, status?: MonitorStatus) => {
    const state = get();
    // 如果正在加载，跳过重复请求
    if (state.isLoading) {
      return;
    }

    try {
      const monitors = await klineMonitorService.getAllRunningMonitors(status, symbol);
      set({ monitors }); // 只更新数据，不设置 isLoading
    } catch (error: any) {
      // 静默刷新失败时不显示错误提示，避免干扰用户
      // 只记录错误，不更新状态
    }
  },

  // 停止监控任务
  stopMonitor: async (monitorId: string) => {
    const { stoppingMonitors } = get();
    if (stoppingMonitors.has(monitorId)) {
      return; // 已经在停止中
    }

    set({
      stoppingMonitors: new Set([...Array.from(stoppingMonitors), monitorId]),
    });

    try {
      await klineMonitorService.stopMonitor(monitorId);

      // 更新监控任务状态为 stopped，而不是移除
      const { monitors } = get();
      set({
        monitors: monitors.map((m) => (m.monitorId === monitorId ? { ...m, status: 'stopped' as const } : m)),
        stoppingMonitors: new Set(Array.from(stoppingMonitors).filter((id) => id !== monitorId)),
      });

      useToastStore.getState().show('监控任务已停止', 'success');
    } catch (error: any) {
      const errorMessage = error.message || '停止监控任务失败';
      set({
        stoppingMonitors: new Set(Array.from(stoppingMonitors).filter((id) => id !== monitorId)),
      });
      useToastStore.getState().show(errorMessage, 'error');
    }
  },

  // 启动监控任务
  startMonitor: async (monitorId: string, symbol: string, bar: BarInterval, enableAutoTrading: boolean = false) => {
    const { startingMonitors } = get();
    if (startingMonitors.has(monitorId)) {
      return; // 已经在启动中
    }

    set({
      startingMonitors: new Set([...Array.from(startingMonitors), monitorId]),
    });

    try {
      await klineMonitorService.restartMonitor(symbol, bar, enableAutoTrading);

      // 更新监控任务状态为 running
      const { monitors } = get();
      set({
        monitors: monitors.map((m) => (m.monitorId === monitorId ? { ...m, status: 'running' as const } : m)),
        startingMonitors: new Set(Array.from(startingMonitors).filter((id) => id !== monitorId)),
      });

      useToastStore.getState().show('监控任务已启动', 'success');
    } catch (error: any) {
      const errorMessage = error.message || '启动监控任务失败';
      set({
        startingMonitors: new Set(Array.from(startingMonitors).filter((id) => id !== monitorId)),
      });
      useToastStore.getState().show(errorMessage, 'error');
    }
  },

  // 删除监控任务
  deleteMonitor: async (monitorId: string) => {
    try {
      await klineMonitorService.deleteMonitor(monitorId);

      // 从列表中移除该监控任务
      const { monitors } = get();
      set({
        monitors: monitors.filter((m) => m.monitorId !== monitorId),
      });

      useToastStore.getState().show('监控任务已删除', 'success');
    } catch (error: any) {
      const errorMessage = error.message || '删除监控任务失败';
      useToastStore.getState().show(errorMessage, 'error');
      throw error;
    }
  },

  // 批量删除停止运行的监控任务
  deleteStoppedMonitors: async () => {
    try {
      const deletedCount = await klineMonitorService.deleteStoppedMonitors();

      // 刷新监控列表
      const state = get();
      await state.refreshMonitors();

      useToastStore.getState().show(`成功删除 ${deletedCount} 个停止运行的监控任务`, 'success');
      return deletedCount;
    } catch (error: any) {
      const errorMessage = error.message || '批量删除监控任务失败';
      useToastStore.getState().show(errorMessage, 'error');
      throw error;
    }
  },

  // 更新单个监控任务的状态（不刷新整个列表）
  updateMonitorStatus: (symbol: string, bar: BarInterval, status: BasicMonitorStatus, monitorId?: string) => {
    const { monitors } = get();
    set({
      monitors: monitors.map((m) => {
        // 通过 symbol 和 bar 匹配监控任务
        if (m.symbol === symbol && m.bar === bar) {
          return {
            ...m,
            status,
            ...(monitorId && { monitorId }), // 如果提供了 monitorId，也更新它
          };
        }
        return m;
      }),
    });
  },

  // 清除错误
  clearError: () => {
    set({ error: null });
  },

  // 获取单个监控任务状态
  getMonitorStatus: async (monitorId: string) => {
    try {
      const monitor = await klineMonitorService.getMonitorStatus(monitorId);

      // 可选：更新本地状态中的对应监控任务
      const { monitors } = get();
      const existingMonitorIndex = monitors.findIndex((m) => m.monitorId === monitorId);

      if (existingMonitorIndex !== -1) {
        // 如果存在，更新它
        const newMonitors = [...monitors];
        newMonitors[existingMonitorIndex] = monitor;
        set({ monitors: newMonitors });
      }

      return monitor;
    } catch (error: any) {
      console.error('获取监控任务状态失败:', error);
      throw error;
    }
  },

  // 获取自动监控状态
  getAutoMonitorStatus: async () => {
    try {
      const status = await klineMonitorService.getAutoMonitorStatus();
      return status;
    } catch (error: any) {
      console.error('获取自动监控状态失败:', error);
      throw error;
    }
  },

  // 获取自动监控配置
  getAutoMonitorConfig: async () => {
    try {
      const config = await klineMonitorService.getAutoMonitorConfig();
      return config;
    } catch (error: any) {
      console.error('获取自动监控配置失败:', error);
      return null;
    }
  },

  // 启动自动监控
  startAutoMonitor: async (
    enableAutoTrading,
    monitorCount,
    excludedSymbols,
    systemPromptId?,
    tradingParams?,
    minVolume?,
    rankingAlgorithm?,
    timeFrame?,
  ) => {
    try {
      await klineMonitorService.startAutoMonitor(
        enableAutoTrading,
        monitorCount,
        excludedSymbols,
        systemPromptId,
        tradingParams,
        minVolume,
        rankingAlgorithm,
        timeFrame,
      );
      useToastStore.getState().show('自动监控已启动', 'success');
    } catch (error: any) {
      const errorMessage = error.message || '启动自动监控失败';
      useToastStore.getState().show(errorMessage, 'error');
      throw error;
    }
  },

  // 停止自动监控
  stopAutoMonitor: async () => {
    try {
      await klineMonitorService.stopAutoMonitor();
      useToastStore.getState().show('自动监控已停止', 'success');
    } catch (error: any) {
      const errorMessage = error.message || '停止自动监控失败';
      useToastStore.getState().show(errorMessage, 'error');
      throw error;
    }
  },
}));
